import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  CustomTableLayout,
  TDocumentDefinitions,
  TFontDictionary,
} from 'pdfmake/interfaces';
import { TCreatedPdf } from 'pdfmake/build/pdfmake';
import {
  MedicalRecordsPdfInput,
  MedicalRecordsPdfOwner,
  MedicalRecordsPdfPet,
  MedicalRecordsPdfShelter,
  MedicalRecordsV2,
  MedicalVisitV2,
  MedicalVaccineV2,
} from './medical-records.models';
import {
  formatMedicalRecordsWeight,
  medicalRecordsWeightUnits,
} from './medical-records-weight';

interface ExtendedPdfMake {
  vfs?: Record<string, string>;
  fonts?: TFontDictionary;
  createPdf(
    documentDefinitions: TDocumentDefinitions,
    tableLayouts?: Record<string, CustomTableLayout>,
    fonts?: TFontDictionary,
    vfs?: Record<string, string>,
  ): TCreatedPdf;
}

const extendedPdfMake = pdfMake as unknown as ExtendedPdfMake;
extendedPdfMake.vfs =
  (
    pdfFonts as unknown as {
      pdfMake?: { vfs?: Record<string, string> };
      vfs?: Record<string, string>;
    }
  ).pdfMake?.vfs ??
  (pdfFonts as unknown as { vfs?: Record<string, string> }).vfs;
extendedPdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

@Injectable()
export class MedicalRecordsPdfService {
  private records: MedicalRecordsV2[] = [];
  private ownerData!: MedicalRecordsPdfOwner;
  private pet!: MedicalRecordsPdfPet;
  private shelterInfo!: MedicalRecordsPdfShelter;
  private filename = 'MedicalRecords';

  // ── Helpers ───────────────────────────────────────────────────────────────
  private formatDate(dateString: string | Date | null): string {
    if (!dateString) return '-';

    const date = new Date(dateString);

    if (date.getFullYear() <= 1 || Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString();
  }

  private formatNotes(notes: string): string {
    return notes
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
  }

  private formatStructuredTreatmentDetails(treatment: {
    dose?: string | number | null;
    quantity?: string | number | null;
    unit?: string | null;
    frequency?: string | null;
    duration?: string | null;
    route?: string | null;
    method?: string | null;
    description?: string | null;
    providerName?: string | null;
  }): string {
    const text = (value: string | number | null | undefined): string =>
      value == null ? '' : String(value).trim();

    const dose = text(treatment.dose);
    const quantity = text(treatment.quantity);
    const unit = text(treatment.unit);
    const frequency = text(treatment.frequency);
    const duration = text(treatment.duration);
    const route = text(treatment.route);
    const method = text(treatment.method);
    const description = text(treatment.description);
    const provider = text(treatment.providerName);

    return [
      dose ? `Dose: ${dose}` : '',
      quantity ? `Quantity: ${quantity}` : '',
      unit ? `Unit: ${unit}` : '',
      frequency ? `Frequency: ${frequency}` : '',
      duration ? `Duration: ${duration}` : '',
      route ? `Route: ${route}` : '',
      method ? `Method: ${method}` : '',
      description ? this.formatNotes(description) : '',
      provider ? `Provider: ${provider}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  private toTitleCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private horizontalRule(width: number, y1 = 0): any {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1,
          x2: width,
          y2: y1,
          lineWidth: 0.5,
          lineColor: '#E6EBEE',
        },
      ],
      margin: [0, 0, 0, 0],
    };
  }

  private mergePetPointDiagnostics(diagnostics: any[] = []): any[] {
    const bySourceId = new Map<string, any>();
    const withoutSourceId: any[] = [];

    const firstValue = (...values: any[]): any =>
      values.find(
        (value) =>
          value !== null &&
          value !== undefined &&
          (typeof value !== 'string' || value.trim() !== ''),
      );

    const firstValidDate = (...values: any[]): any =>
      values.find((value) => {
        if (!value) return false;

        const date = new Date(value);

        return !Number.isNaN(date.getTime()) && date.getFullYear() > 1;
      });

    diagnostics.forEach((diagnostic) => {
      const sourceId = diagnostic.sourceId?.toString().trim();

      if (!sourceId) {
        withoutSourceId.push(diagnostic);
        return;
      }

      const existing = bySourceId.get(sourceId);

      if (!existing) {
        bySourceId.set(sourceId, { ...diagnostic });
        return;
      }

      bySourceId.set(sourceId, {
        ...existing,
        ...diagnostic,

        testName: firstValue(existing.testName, diagnostic.testName),
        testNameRaw: firstValue(existing.testNameRaw, diagnostic.testNameRaw),

        result: firstValue(existing.result, diagnostic.result),
        resultType: firstValue(existing.resultType, diagnostic.resultType),

        testDate: firstValidDate(
          existing.testDate,
          diagnostic.testDate,
          existing.resultDate,
          diagnostic.resultDate,
        ),

        resultDate: firstValidDate(
          existing.resultDate,
          diagnostic.resultDate,
          existing.testDate,
          diagnostic.testDate,
        ),

        providerName: firstValue(
          existing.providerName,
          diagnostic.providerName,
        ),

        manufacturerName: firstValue(
          existing.manufacturerName,
          diagnostic.manufacturerName,
        ),

        manufacturerNameRaw: firstValue(
          existing.manufacturerNameRaw,
          diagnostic.manufacturerNameRaw,
        ),

        description: firstValue(existing.description, diagnostic.description),

        sourceId,

        sourceLastUpdated: firstValidDate(
          existing.sourceLastUpdated,
          diagnostic.sourceLastUpdated,
        ),
      });
    });

    return [...bySourceId.values(), ...withoutSourceId];
  }

  // ── PDF generation ────────────────────────────────────────────────────────
  download(input: MedicalRecordsPdfInput): void {
    this.records = input.records;
    this.ownerData = input.owner;
    this.pet = input.pet;
    this.shelterInfo = input.shelter;
    this.filename = input.filename;

    const matchedSite = input.shelterSites.find(
      (site) =>
        site.openToPublic &&
        !!site.locations.find((l) => l.location === this.pet.location),
    );

    const rawRecords = this.records;

    if (!rawRecords.length) {
      throw new Error('No V2 records available');
    }

    // Flatten all visits across all records, sorted newest first
    const allVisits: MedicalVisitV2[] = rawRecords
      .flatMap((record) => record.visits ?? [])
      .sort(
        (a: MedicalVisitV2, b: MedicalVisitV2) =>
          new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime(),
      );

    // Flatten all vaccines across all visits for the vaccine table
    const allVaccines: MedicalVaccineV2[] = allVisits
      .flatMap((v) => v.vaccines ?? [])
      .filter((v) => {
        const status = v.status?.trim().toLowerCase();

        return (v.vaccineNameRaw || v.vaccineName) && status !== 'scheduled';
      })
      .sort(
        (a, b) =>
          new Date(b.vaccinationDate ?? 0).getTime() -
          new Date(a.vaccinationDate ?? 0).getTime(),
      );

    let street: string;

    if (matchedSite) {
      street = [
        matchedSite.physicalAddress?.streetLine1,
        matchedSite.physicalAddress?.streetLine2,
      ]
        .filter(Boolean)
        .join(', ');
    } else {
      street = [
        this.shelterInfo.mailingAddress?.streetLine1,
        this.shelterInfo.mailingAddress?.streetLine2,
      ]
        .filter(Boolean)
        .join(', ');
    }

    let cityState: string;

    if (matchedSite) {
      cityState = [
        `${matchedSite.physicalAddress?.city}, ${matchedSite.physicalAddress?.stateProvince}, ${matchedSite.physicalAddress?.zipCode}`,
        matchedSite.physicalAddress?.country,
      ]
        .filter(Boolean)
        .join(', ');
    } else {
      cityState = [
        `${this.shelterInfo.mailingAddress?.city}, ${this.shelterInfo.mailingAddress?.stateProvince}, ${this.shelterInfo.mailingAddress?.zipCode}`,
        this.shelterInfo.mailingAddress?.country,
      ]
        .filter(Boolean)
        .join(', ');
    }

    const ownerName = `${this.ownerData?.firstname} ${this.ownerData?.lastname}`;
    const ownerAddr = `${this.ownerData?.address.city}, ${this.ownerData?.address.stateProvince}, ${this.ownerData?.address.zipCode}`;

    const {
      sex,
      altered,
      animalType: species,
      breed,
      color,
      dateOfBirth,
      weightHistory,
      microChip,
      microChipIssuer,
    } = this.pet;
    const weight = weightHistory[weightHistory.length - 1]?.weightLbs || 0;
    const weightUnit = input.weightUnit;
    let weightStr = '';

    if (weight) {
      weightStr = formatMedicalRecordsWeight(
        weight,
        medicalRecordsWeightUnits.lbs,
        weightUnit,
      );
    }

    const formattedDOB = new Date(dateOfBirth).toLocaleDateString();

    // ── Vaccine table ────────────────────────────────────────────────────
    const hasNextDue = allVaccines.some(
      (v) => this.formatDate(v.reVaccinationDate) !== '-',
    );
    const hasVaccineType = allVaccines.some((v) => v.type?.trim());
    const vaccineHeaders: any[] = [
      {
        text: 'Vaccinations',
        bold: true,
        style: 'header2',
        color: '#033557',
        margin: [0, 5],
      },
    ];

    const vaccineWidths: string[] = ['*'];

    if (hasVaccineType) {
      vaccineHeaders.push({
        text: 'Type',
        bold: true,
        style: 'header2',
        color: '#033557',
        margin: [0, 5],
      });
      vaccineWidths.push('*');
    }

    vaccineHeaders.push({
      text: 'Vaccinated',
      bold: true,
      style: 'header2',
      color: '#033557',
      margin: [0, 5],
    });
    vaccineWidths.push('*');

    if (hasNextDue) {
      vaccineHeaders.push({
        text: 'Next Due',
        bold: true,
        style: 'header2',
        margin: [0, 5],
        color: '#033557',
      });
      vaccineWidths.push('*');
    }

    const vaccineRows = allVaccines.map((v) => {
      const row: any[] = [
        {
          text: v.vaccineNameRaw || v.vaccineName || '-',
          fontSize: 11,
          color: '#2E5874',
        },
      ];

      if (hasVaccineType) {
        row.push({
          text: v.type || '-',
          fontSize: 11,
          color: '#2E5874',
        });
      }

      row.push({
        text: this.formatDate(v.vaccinationDate),
        fontSize: 11,
        color: '#2E5874',
      });

      if (hasNextDue) {
        row.push({
          text: this.formatDate(v.reVaccinationDate),
          fontSize: 11,
          color: '#2E5874',
        });
      }

      return row;
    });

    const vaccineTable = allVaccines.length
      ? {
          margin: [0, 0, 0, 0],
          table: {
            widths: vaccineWidths,
            body: [vaccineHeaders, ...vaccineRows],
          },
          layout: {
            fillColor: (i: number) => (i === 0 ? '#e7ecf3' : null),
            hLineWidth: (i: number, node: any) =>
              i === node.table.body.length ? 0 : 1,
            lineHeight: (r: number) => (r === 1 ? 2 : 1),
            vLineWidth: () => 0,
            hLineColor: () => '#CCD6DD',
            vLineColor: () => '#CCD6DD',
          },
        }
      : {
          margin: [0, 0, 0, 0],
          table: {
            widths: ['*'],
            body: [
              [
                {
                  text: 'No vaccinations recorded',
                  alignment: 'center',
                  color: '#2E5874',

                  border: [false, false, false, false],
                },
              ],
            ],
          },
        };

    // ── Visit sections ────────────────────────────────────────────────────
    const visitSections: any[] = [];
    const shelterSoftware = (this.shelterInfo as any)
      ?.shelterManagementSoftware;
    const isPetPoint = shelterSoftware === 'PetPoint';
    for (const visit of allVisits) {
      // Visit header row
      visitSections.push({
        columns: [
          {
            width: '*',
            stack: [
              {
                text: this.formatDate(visit.visitDate),
                style: 'subheader2',
                margin: [0, 0, 0, 0],
                fillColor: '#E6EBEE',
                bold: true,
                color: '#033557',
              },
            ],
          },
          {
            width: '*',
            stack: [
              {
                text: (() => {
                  const raw = visit.visitTypeRaw;
                  const subTypeRaw = visit.visitSubTypeRaw;
                  if (raw === 'SURG') {
                    const surgeryType = visit.treatments?.[0]?.treatmentTypeRaw;
                    return `Surgery Type: ${surgeryType ? this.toTitleCase(surgeryType) : 'Surgery'}`;
                  }
                  if (raw === 'FEED') {
                    const feedType = visit.conditions?.[0]?.conditionType;
                    return `Feed Type: ${feedType ? this.toTitleCase(feedType) : 'Feed'}`;
                  }
                  // EXAM and everything else — use visitTypeRaw as the type, not condition
                  const examType = subTypeRaw || raw;
                  return `Exam Type: ${examType ? this.toTitleCase(examType) : 'Exam'}`;
                })(),
                style: 'subheader2',
                margin: [0, 0, 0, 0],
                fillColor: '#E6EBEE',
                bold: true,
                color: '#033557',
              },
            ],
          },
        ],
      });

      // Vitals
      const vitals: string[] = [];
      const visitVitals = visit.vitals;

      if (visitVitals) {
        const weight =
          visitVitals.weightRaw?.trim() ||
          (visitVitals.weight != null
            ? `${visitVitals.weight} ${weightUnit}`
            : null);

        const pulse = visitVitals.pulseRaw?.trim() || visitVitals.pulse;

        const temperature =
          visitVitals.temperatureRaw?.trim() || visitVitals.temperature;

        const respirationRate =
          visitVitals.respirationRateRaw?.trim() || visitVitals.respirationRate;

        if (weight) {
          vitals.push(`Weight: ${weight}`);
        }

        if (pulse != null && pulse !== '') {
          vitals.push(`Pulse: ${pulse}`);
        }

        if (visitVitals.heartRate != null && visitVitals.heartRate !== '') {
          vitals.push(`Heart Rate: ${visitVitals.heartRate}`);
        }

        if (temperature != null && temperature !== '') {
          const temperatureMethod = visitVitals.temperatureMethod?.trim();

          vitals.push(
            `Temperature: ${temperature}${
              temperatureMethod ? ` (${temperatureMethod})` : ''
            }`,
          );
        }

        if (respirationRate != null && respirationRate !== '') {
          vitals.push(`Respirations: ${respirationRate}`);
        }
      }
      // Conditions
      // Split treatments: Condition type → conditions panel, others → treatments panel
      const conditionTreatments = (visit.treatments ?? []).filter(
        (t) => t.treatmentType === 'Condition',
      );
      const medicationTreatments = (visit.treatments ?? []).filter(
        (t) => t.treatmentType === 'Medication',
      );
      const nonConditionTreatments = (visit.treatments ?? []).filter(
        (t) =>
          t.treatmentType !== 'Condition' && t.treatmentType !== 'Medication',
      );

      // Conditions — merge visit.conditions + treatmentType==='Condition' entries
      const conditionLines: string[] = [];

      if (visit.conditions?.length) {
        visit.conditions.forEach((c) => {
          const type = c.conditionType ? this.toTitleCase(c.conditionType) : '';
          if (type) conditionLines.push(type);
        });
      }

      conditionTreatments.forEach((t) => {
        const name = t.condition ?? t.treatmentNameRaw ?? '';
        if (name) conditionLines.push(name);
      });

      const conditionText = conditionLines.length
        ? conditionLines.join('\n')
        : 'No conditions recorded';

      visitSections.push({
        columns: [
          {
            width: '*',
            stack: [
              {
                text: 'Conditions',
                style: 'subheader2',
                fillColor: '#E6EBEE',
                margin: [0, 5, 0, 0],
                italics: true,
                color: '#377D80',
              },
              {
                text: conditionText,
                style: 'text',
                fontSize: 11,
                margin: [0, 0, 0, 10],
                color: '#2E5874',
              },
            ],
          },
          {
            width: '*',
            stack:
              vitals.length > 0
                ? [
                    {
                      text: 'Vitals',
                      style: 'subheader2',
                      fillColor: '#E6EBEE',
                      margin: [0, 5, 0, 0],
                      italics: true,
                      color: '#377D80',
                    },
                    ...vitals.map((v) => ({
                      text: v,
                      style: 'text',
                      fontSize: 11,
                      margin: [0, 0, 0, 0],
                      color: '#2E5874',
                    })),
                  ]
                : [],
          },
        ],
      });

      // Treatments
      const treatments = nonConditionTreatments;
      if (treatments.length) {
        visitSections.push({
          stack: [
            {
              columns: [
                {
                  width: '*',
                  text: 'Treatments',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '*',
                  text: 'Notes',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '20%',
                  text: 'Date Administered',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
              ],
            },
            this.horizontalRule(515),
            ...treatments.map((t) => ({
              stack: [
                {
                  columns: [
                    {
                      width: '*',
                      text:
                        t.treatmentNameRaw?.trim() ||
                        t.treatmentName?.trim() ||
                        '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '*',
                      text: isPetPoint
                        ? t.description?.trim()
                          ? this.formatNotes(t.description)
                          : t.dose?.trim()
                            ? t.dose.trim()
                            : t.route?.trim() ||
                              t.treatmentTypeRaw?.trim() ||
                              t.treatmentType?.trim() ||
                              '-'
                        : t.recurrenceSummary?.trim() ||
                          this.formatStructuredTreatmentDetails(t) ||
                          t.treatmentTypeRaw?.trim() ||
                          t.treatmentType?.trim() ||
                          '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '20%',
                      text: this.formatDate(t.treatmentDate),
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                  ],
                  margin: [0, 0, 0, 0],
                },
                this.horizontalRule(515, 5),
              ],
            })),
          ],
          margin: [0, 0, 0, 10],
        });
      }
      const diagnostics = isPetPoint
        ? this.mergePetPointDiagnostics(visit.diagnostics ?? [])
        : (visit.diagnostics ?? []);

      const visibleDiagnostics = diagnostics.filter(
        (d) => d.testNameRaw?.trim() || d.testName?.trim() || d.result,
      );

      if (visibleDiagnostics.length) {
        visitSections.push({
          stack: [
            {
              columns: [
                {
                  width: '*',
                  text: 'Diagnostics',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '*',
                  text: 'Notes',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '20%',
                  text: 'Date',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
              ],
            },
            this.horizontalRule(515),
            ...visibleDiagnostics.map((d) => ({
              stack: [
                {
                  columns: [
                    {
                      width: '*',
                      text: d.testNameRaw?.trim() || d.testName?.trim() || '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '*',
                      text: d.result?.trim() || d.testType?.trim() || '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '20%',
                      text: this.formatDate(d.testDate),
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                  ],
                  margin: [0, 0, 0, 0],
                },
                this.horizontalRule(515, 5),
              ],
            })),
          ],
          margin: [0, 0, 0, 10],
        });
      }
      if (medicationTreatments.length) {
        visitSections.push({
          stack: [
            {
              columns: [
                {
                  width: '*',
                  text: 'Medications',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '*',
                  text: 'Dosage / Instructions',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  width: '20%',
                  text: 'Date Administered',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
              ],
            },
            this.horizontalRule(515),
            ...medicationTreatments.map((t) => ({
              stack: [
                {
                  columns: [
                    {
                      width: '*',
                      text:
                        t.treatmentNameRaw?.trim() ||
                        t.treatmentName?.trim() ||
                        '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '*',
                      text: isPetPoint
                        ? t.description?.trim()
                          ? this.formatNotes(t.description)
                          : t.dose?.trim()
                            ? t.dose.trim()
                            : t.route?.trim() ||
                              t.treatmentTypeRaw?.trim() ||
                              t.treatmentType?.trim() ||
                              '-'
                        : t.recurrenceSummary?.trim() ||
                          this.formatStructuredTreatmentDetails(t) ||
                          t.treatmentTypeRaw?.trim() ||
                          t.treatmentType?.trim() ||
                          '-',
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                    {
                      width: '20%',
                      text: this.formatDate(t.treatmentDate),
                      style: 'text',
                      fontSize: 11,
                      color: '#2E5874',
                    },
                  ],
                  margin: [0, 0, 0, 0],
                },
                this.horizontalRule(515, 5),
              ],
            })),
          ],
          margin: [0, 0, 0, 10],
          color: '#033557',
        });
      }
      // Notes / diagnostics description
      const petPointTreatmentDescription = isPetPoint
        ? visit.treatments?.find((t) => t.description?.trim())?.description
        : null;

      const notes =
        petPointTreatmentDescription ??
        visit.diagnostics?.find((d) => d.description?.trim())?.description ??
        visit.description;

      if (notes?.trim()) {
        visitSections.push({
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: 'Notes',
                  style: 'subheader2',
                  fillColor: '#E6EBEE',
                  margin: [0, 5, 0, 0],
                  italics: true,
                  color: '#377D80',
                },
                {
                  text: this.formatNotes(notes),
                  style: 'text',
                  fontSize: 11,
                  margin: [0, 0, 0, 10],
                  color: '#2E5874',
                },
              ],
            },
          ],
        });
      }

      // Separator between visits
      visitSections.push({
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 0,
            x2: 500,
            y2: 0,
            lineWidth: 20,
            color: '#2E5874',
          },
        ],
        margin: [0, 0, 0, 10],
      });
    }
    const shelterAnimalSourceDisplayId = rawRecords
      .map((record) => record?.shelterAnimalSourceDisplayId)
      .find((id) => id != null && String(id).trim());

    const petNameWithSourceId = shelterAnimalSourceDisplayId
      ? `${this.pet.name} (${String(shelterAnimalSourceDisplayId).trim()})`
      : this.pet.name;
    const docDefinition: any = {
      content: [
        {
          text: 'Medical Summary Report',
          style: 'title',
          margin: [0, 0, 0, 13.5],
        },
        {
          text: matchedSite
            ? matchedSite.siteName
            : this.shelterInfo?.legalName,
          style: 'header',
          margin: [0, 0, 0, 2],
          color: '#033557',
        },
        {
          text: street,
          style: 'address',
          margin: [0, 0, 0, 2],
          color: '#033557',
        },
        {
          text: cityState,
          style: 'address',
          margin: [0, 0, 0, 2],
          color: '#033557',
        },
        {
          text: this.shelterInfo?.phoneNumber,
          style: 'phone',
          margin: [0, 0, 0, 8.5],
          color: '#033557',
        },
        {
          columns: [
            {
              width: '40%',
              stack: [
                {
                  text: 'Owner Info',
                  style: 'subTitleText',
                  margin: [0, 0, 0, 3],
                },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: 0,
                      x2: 180,
                      y2: 0,
                      lineWidth: 1,
                      lineColor: '#CCD6DD',
                    },
                  ],
                },
                {
                  margin: [0, 0, 0, 0],
                  layout: 'noBorders',
                  table: {
                    body: [
                      [
                        {
                          text: 'Name: ' + ownerName,
                          style: 'ownerAnimalText',
                        },
                      ],
                      [
                        {
                          text: 'Email: ' + this.ownerData.email,
                          style: 'ownerAnimalText',
                        },
                      ],
                      [
                        {
                          text: 'Phone: ' + (this.ownerData.cellPhone ?? ''),
                          style: 'ownerAnimalText',
                        },
                      ],
                      [
                        {
                          text:
                            'Address: ' + this.ownerData.address.streetLine1,
                          style: 'ownerAnimalText',
                        },
                      ],
                      [
                        {
                          text:
                            (this.ownerData.address.streetLine2 ?? '') +
                            ownerAddr,
                          style: 'ownerAnimalText',
                        },
                      ],
                    ],
                  },
                },
              ],
            },
            {
              width: '60%',
              stack: [
                {
                  text: 'Animal Info',
                  style: 'subTitleText',
                  margin: [0, 0, 0, 3],
                },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: 0,
                      x2: 250,
                      y2: 0,
                      lineWidth: 1,
                      lineColor: '#CCD6DD',
                    },
                  ],
                },
                {
                  margin: [0, 3, 0, 0],
                  layout: 'noBorders',
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        {
                          text: 'Name: ' + petNameWithSourceId,
                          style: 'ownerAnimalText',
                        },
                        { text: 'Gender: ' + sex, style: 'ownerAnimalText' },
                      ],
                      [
                        {
                          text: 'Altered: ' + altered,
                          style: 'ownerAnimalText',
                        },
                        {
                          text: 'Species: ' + species,
                          style: 'ownerAnimalText',
                        },
                      ],
                      [
                        { text: 'Breed: ' + breed, style: 'ownerAnimalText' },
                        { text: 'Color: ' + color, style: 'ownerAnimalText' },
                      ],
                      [
                        {
                          text: 'DOB: ' + formattedDOB,
                          style: 'ownerAnimalText',
                        },
                        {
                          text: `Weight: ${weightStr}`,
                          style: 'ownerAnimalText',
                        },
                      ],
                    ],
                  },
                },
                {
                  layout: 'noBorders',
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        {
                          text: 'Microchip #: ' + microChip,
                          style: 'ownerAnimalText',
                        },
                        { text: microChipIssuer, style: 'ownerAnimalText' },
                      ],
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          width: '100%',
          stack: [
            {
              text: 'Medical Summary',
              style: 'subTitleText',
              margin: [0, 10, 0, 3],
            },
          ],
        },
        vaccineTable,
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: ['*', '*'],
            body: [
              [
                {
                  text: 'Visit Date',
                  bold: true,
                  style: 'header2',
                  margin: [0, 5],
                  color: '#033557',
                },
                {
                  text: 'Medical Status',
                  bold: true,
                  style: 'header2',
                  margin: [0, 5],
                  color: '#033557',
                },
              ],
            ],
          },
          layout: {
            fillColor: (i: number) => (i === 0 ? '#e7ecf3' : null),
            hLineWidth: () => 1,
            vLineWidth: () => 0,
            hLineColor: () => '#CCD6DD',
            vLineColor: () => '#CCD6DD',
          },
        },
        { columns: [{ widths: ['*', '*'], stack: visitSections }] },
      ],
      defaultStyle: { font: 'Roboto' },
      styles: {
        ownerAnimalText: {
          fontSize: 10,
          color: '#2E5874',
          font: 'Roboto',
        },
        title: {
          fontSize: 16,
          bold: true,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        header: {
          fontSize: 15,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        subheader2: {
          fontSize: 12,
          font: 'Roboto',
          fillColor: '#E6EBEE',
          colSpan: 2,
        },
        subheader: {
          fontSize: 15,
          bold: true,
          margin: [0, 10, 0, 5],
          font: 'Roboto',
          color: '#033557',
        },
        normal: { margin: [0, 5, 0, 5], font: 'Roboto' },
        address: {
          fontSize: 9,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        phone: {
          fontSize: 9,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        name: { fontSize: 20, bold: true, font: 'Roboto', color: '#033557' },
        subTitleText: {
          fontSize: 16,
          bold: true,
          font: 'Roboto',
          color: '#033557',
        },
      },
    };

    extendedPdfMake.createPdf(docDefinition).download(this.filename);
  }
}
